import styled from 'styled-components';
import { Map } from '../map';
import React, { useState } from 'react';
import Cluster from '@urbica/react-map-gl-cluster';
import { Marker, Popup } from '@urbica/react-map-gl';
import { motion } from 'framer-motion';
import { getColor, getIcon } from './utils';
import { colors } from '../../utils/theme';
import { FaCheckCircle, FaInfoCircle, FaHeadphonesAlt, FaPhoneAlt, FaWalking } from 'react-icons/fa';

export const ClusterMarker = React.memo<{ latitude: number; longitude: number; pointCount: number }>(
  ({ longitude, latitude, pointCount }) => (
    <Marker longitude={longitude} latitude={latitude}>
      <div
        style={{
          width: pointCount > 99 ? '30px' : pointCount > 9 ? '25px' : '20px',
          height: pointCount > 99 ? '30px' : pointCount > 9 ? '25px' : '20px',
          color: '#fff',
          background: '#1978c8',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          backgroundColor: colors.lightGreen,
        }}>
        <p style={{ height: 18 }}>{pointCount}</p>
      </div>
    </Marker>
  ),
  (prev, next) => {
    return prev.pointCount === next.pointCount;
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MapContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const MapBottomContent = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  position: absolute;
  bottom: 0;
  z-index: 2;
`;

const Layer = styled.div`
  height: 42px;
  display: flex;
  align-items: center;
  padding: 8px;
  width: 100%;
  background-color: ${colors.white};
`;

const List = styled.div<{ open: boolean }>`
  height: ${p => (p.open ? 220 : 0)}px;
  padding: ${p => (p.open ? 8 : 0)}px;
  transition: 0.5s;
  width: 100%;
  background-color: #ffffffaa;
  overflow-y: auto;
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div<{ backgroundColor: string }>`
  width: 32px;
  height: 32px;
  border-radius: 40px;
  margin: 6px 12px 4px 12px;
  background-color: ${p => p.backgroundColor};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const Legend = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 0.1px solid #1110102a;
`;

const ItemContainer = styled.a`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid #1110102a;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.43;
  letter-spacing: 0.01071em;
`;

const Text = styled.div`
  padding: 16px 0;
  width: 100%;
  color: ${colors.black};
`;

const ClickContainer = styled.div`
  margin-top: 8px;
  width: 96px;
  display: flex;
  justify-content: space-around;
`;

const MobileUseful = ({ categories, onClickCategory, selectedCategories, importantNumbers, geojson, viewport }) => {
  const [click, setClick] = useState<any>();
  const [legendOpen, setLegendOpen] = useState(false);
  const [usefulOpen, setUsefulOpen] = useState(false);

  return (
    <Container>
      <MapContainer>
        <Map style={{ height: 'calc(100vh - 300px)' }} viewport={viewport}>
          {geojson && (
            <Cluster radius={40} extent={512} nodeSize={64} component={ClusterMarker}>
              {geojson.features.map(point => (
                <Marker key={point.properties.id} longitude={point.geometry.coordinates[0]} latitude={point.geometry.coordinates[1]}>
                  <motion.div
                    onClick={() => {
                      setClick(point);
                    }}
                    initial="rest"
                    whileHover="hover"
                    animate="rest">
                    <motion.div
                      variants={{
                        hover: {
                          scale: 1.5,
                        },
                        rest: {
                          scale: 1,
                        },
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 48,

                        backgroundColor: getColor(point.properties.category),
                      }}>
                      <img width={36} height={36} src={getIcon(point.properties.category)} alt={point.properties.title} />
                    </motion.div>
                  </motion.div>
                </Marker>
              ))}
            </Cluster>
          )}

          {click && (
            <Popup
              longitude={click.geometry.coordinates[0]}
              latitude={click.geometry.coordinates[1]}
              anchor={'bottom'}
              closeButton={true}
              closeOnClick={false}
              onClose={() => {
                setClick(undefined);
              }}
              offset={40}>
              <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <div style={{ maxWidth: 150, textAlign: 'center', marginBottom: 6 }}>{click.properties.title}</div>
                <ClickContainer>
                  <a href={`https://www.google.es/maps?q=${click.geometry.coordinates[1]},${click.geometry.coordinates[0]}`} target="blank">
                    <FaWalking className="text-2xl" style={{ width: 16, color: colors.black }} />
                  </a>
                  <a href={`tel:${click.properties.phone}`}>
                    <FaPhoneAlt className="text-2xl" style={{ width: 16, color: colors.black }} />
                  </a>
                </ClickContainer>
              </div>
            </Popup>
          )}
        </Map>
        <MapBottomContent>
          <Layer onClick={() => setLegendOpen(!legendOpen)}>
            <FaInfoCircle className="text-2xl" style={{ marginRight: 8, color: colors.black }} /> LEGEND
          </Layer>
          <List open={legendOpen}>
            {categories.map(c => (
              <Legend key={c} onClick={() => onClickCategory(c)}>
                <InfoContainer>
                  <IconContainer backgroundColor={getColor(c)}>
                    <img width={18} height={18} src={getIcon(c)} alt={c} />
                  </IconContainer>
                  <div>{c}</div>
                </InfoContainer>
                {selectedCategories?.includes(c) && <FaCheckCircle />}
              </Legend>
            ))}
          </List>
          <Layer onClick={() => setUsefulOpen(!usefulOpen)}>
            <FaHeadphonesAlt className="text-2xl" style={{ marginRight: 8, color: colors.black }} />
            USEFUL NUMBERS
          </Layer>
          <List open={usefulOpen}>
            {importantNumbers?.map(n => (
              <ItemContainer key={n.phone} href={`tel:${n?.phone}`}>
                <FaInfoCircle className="text-2xl" style={{ marginRight: 16, marginLeft: 16, color: colors.black }} />
                <Text>{n.title}</Text>
                <FaPhoneAlt className="text-2xl" style={{ width: 16, marginRight: 8, color: colors.black }} />
              </ItemContainer>
            ))}
          </List>
        </MapBottomContent>
      </MapContainer>
    </Container>
  );
};

export default MobileUseful;
